import { buildDemoStudentsForClass } from "./buildDemoStudents";
import {
  DEMO_TURMA_DISC,
  DEMO_TURMA_DISC_HAB
} from "./buildDemoOccurrences";
import { DEMO_SCENARIO, DEMO_SCENARIO_CLASS_ID } from "./demoScenario";
import {
  buildDemoHabReportConcepts,
  buildDemoHabReportData,
  buildDemoHabReportListEntries,
  buildDemoHabReportPeriods
} from "./buildDemoHabilidadesReport";

/**
 * Indicadores do relatório — habilidades observadas típicas da Educação Infantil (demo).
 * IDs estáveis para `student.concepts` e para o salvamento (CODETAPA / CODPROVA).
 */
const DEMO_INDEXES = [
  {
    id: "idx-ei-mot",
    name: "Brincadeiras com movimento e coordenação motora",
    TIPOETAPA: "N",
    CODETAPA: "ET1",
    CODPROVA: "P1"
  },
  {
    id: "idx-ei-aut",
    name: "Autonomia nos cuidados pessoais e rotinas",
    TIPOETAPA: "N",
    CODETAPA: "ET1",
    CODPROVA: "P1"
  },
  {
    id: "idx-ei-soc",
    name: "Convivência, escuta e interação com os pares",
    TIPOETAPA: "N",
    CODETAPA: "ET1",
    CODPROVA: "P2"
  },
  {
    id: "idx-ei-ling",
    name: "Linguagem oral, vocabulário e expressão de ideias",
    TIPOETAPA: "N",
    CODETAPA: "ET1",
    CODPROVA: "P2"
  },
  {
    id: "idx-ei-exp",
    name: "Exploração de materiais, cores, texturas e natureza",
    TIPOETAPA: "N",
    CODETAPA: "ET1",
    CODPROVA: "P2"
  },
  {
    id: "idx-ei-art",
    name: "Expressão artística, musical e corporal",
    TIPOETAPA: "N",
    CODETAPA: "ET1",
    CODPROVA: "P2"
  },
  {
    id: "idx-ei-cur",
    name: "Curiosidade, atenção e participação nas propostas",
    TIPOETAPA: "N",
    CODETAPA: "ET1",
    CODPROVA: "P2"
  }
];

const CONCEPT_ROTATION = ["demo_ir_c1", "demo_ir_c2", "demo_ir_c3"];

/**
 * GET ClassDiaryV1/individual-report/periods
 */
export function buildDemoIndReportPeriods(idTurmaDisc) {
  if (String(idTurmaDisc) === String(DEMO_TURMA_DISC_HAB)) {
    return buildDemoHabReportPeriods();
  }
  if (String(idTurmaDisc) !== String(DEMO_TURMA_DISC)) {
    return [];
  }
  return [
    {
      id: "1BIM",
      name: "1º bimestre — demonstração",
      current: true
    }
  ];
}

/**
 * GET ClassDiaryV1/individual-report/concepts (o serviço acrescenta "Sem conceito").
 *
 * Aceita `idTurmaDisc` opcional para permitir paletas por disciplina —
 * o template "RELATÓRIO DE HABILIDADES" usa C/CO/AC/NA, enquanto o padrão
 * segue Excelente/Bom/Regular.
 */
export function buildDemoIndReportConcepts(idTurmaDisc) {
  if (String(idTurmaDisc) === String(DEMO_TURMA_DISC_HAB)) {
    return buildDemoHabReportConcepts();
  }
  return [
    {
      id: "demo_ir_c1",
      label: "Excelente",
      color: "#c8e6c9",
      idConcept: 9001,
      idGroup: 10
    },
    {
      id: "demo_ir_c2",
      label: "Bom",
      color: "#fff9c4",
      idConcept: 9002,
      idGroup: 10
    },
    {
      id: "demo_ir_c3",
      label: "Regular",
      color: "#ffe0b2",
      idConcept: 9003,
      idGroup: 10
    }
  ];
}

/**
 * GET ClassDiaryV1/individual-report — corpo esperado por `IndividualReport.setupTableProps`.
 */
export function buildDemoIndReportData(idTurmaDisc, period) {
  if (String(idTurmaDisc) === String(DEMO_TURMA_DISC_HAB)) {
    return buildDemoHabReportData(idTurmaDisc, period);
  }
  if (String(idTurmaDisc) !== String(DEMO_TURMA_DISC)) {
    return { axis: [], students: [] };
  }

  const students = buildDemoStudentsForClass("DEMO-T1").map((s, i) => {
    const concepts = DEMO_INDEXES.map((ix, j) => ({
      id: ix.id,
      value: CONCEPT_ROTATION[(i + j) % CONCEPT_ROTATION.length]
    }));
    return {
      rollCallNum: i + 1,
      ra: s.ra,
      name: s.name,
      photo: s.photo,
      absense: i % 5,
      concepts
    };
  });

  return {
    axis: [
      {
        name: "Educação Infantil — desenvolvimento e aprendizagem (demonstração)",
        indexes: DEMO_INDEXES
      }
    ],
    students
  };
}

/**
 * Lista de relatórios existentes para a tela intermediária `IndividualReportList`.
 * Cada item é a combinação (disciplina × período); em demo devolvemos apenas o
 * par pré-carregado quando o cenário pede `seedExistingReport`.
 */
export function buildDemoIndReportList(classId) {
  if (classId !== DEMO_SCENARIO_CLASS_ID) return [];
  const scenario = DEMO_SCENARIO.individualReport || {};
  if (!scenario.seedExistingReport) return [];

  const periods = buildDemoIndReportPeriods(DEMO_TURMA_DISC);
  const data = buildDemoIndReportData(DEMO_TURMA_DISC);
  const students = data.students || [];
  const indexes = (data.axis || []).reduce(
    (acc, ax) => acc + (ax.indexes ? ax.indexes.length : 0),
    0
  );

  const totalCells = students.length * indexes;
  const filledCells = students.reduce((acc, s) => {
    const filled = (s.concepts || []).filter(
      c => c.value && c.value !== "demo_ir_sem"
    ).length;
    return acc + filled;
  }, 0);
  const filledPercent =
    totalCells === 0 ? 0 : Math.round((filledCells / totalCells) * 100) / 100;

  const portugueseEntries = periods.map(period => ({
    reportKey: `${DEMO_TURMA_DISC}::${period.id}`,
    classId,
    discipline: {
      idTurmaDisc: DEMO_TURMA_DISC,
      name: "Português — demonstração"
    },
    period,
    studentsCount: students.length,
    indexesCount: indexes,
    filledPercent,
    updatedAt: new Date().toISOString()
  }));

  return [...portugueseEntries, ...buildDemoHabReportListEntries(classId)];
}
